import { supabase } from "../lib/supabase";
import type { Json } from "../types/supabase";
import type { SearchParams } from "../components/Manager/types";

export type StoreActivityForManager = {
  sa_id: number;
  store_id: number;
  store_name: string;
  store_reference_number: string;
  ma_id: number;
  period_id: number;
  period_name: string;
  period_start_date: string;
  period_end_date: string;
  product_id: number;
  product_name: string;
  product_code: string;
  brand_id: number;
  brand_name: string;
  supplier_id: number;
  supplier_name: string;
  agency_id: number;
  agency_name: string;
  rep_names: string | null;
  is_deleted: boolean;
  is_fulfilled: boolean;
  total_count: number;
};

export type FetchResult<T> = {
  data: T[];
  hasMore: boolean;
  totalCount?: number;
};

export async function fetchStoreActivities(
  limit?: number,
  offset?: number,
  searchParams?: SearchParams,
): Promise<FetchResult<StoreActivityForManager>> {
  const filters = searchParams?.filters ?? {};

  // Build filter object for the RPC
  const rpcFilters: Record<string, unknown> = {};

  if (filters.period_ids && Array.isArray(filters.period_ids)) {
    rpcFilters.period_ids = filters.period_ids;
  } else if (filters.period_id) {
    rpcFilters.period_ids = [filters.period_id];
  }

  if (filters.product_ids && Array.isArray(filters.product_ids)) {
    rpcFilters.product_ids = filters.product_ids;
  } else if (filters.product_id) {
    rpcFilters.product_ids = [filters.product_id];
  }

  if (filters.supplier_ids && Array.isArray(filters.supplier_ids)) {
    rpcFilters.supplier_ids = filters.supplier_ids;
  } else if (filters.supplier_id) {
    rpcFilters.supplier_ids = [filters.supplier_id];
  }

  if (filters.store_ids && Array.isArray(filters.store_ids)) {
    rpcFilters.store_ids = filters.store_ids;
  } else if (filters.store_id) {
    rpcFilters.store_ids = [filters.store_id];
  }

  if (filters.user_ids && Array.isArray(filters.user_ids)) {
    rpcFilters.user_ids = filters.user_ids;
  } else if (filters.user_id) {
    rpcFilters.user_ids = [filters.user_id];
  }

  if (filters.agency_ids && Array.isArray(filters.agency_ids)) {
    rpcFilters.agency_ids = filters.agency_ids;
  }

  const { data, error } = await supabase.rpc(
    "fetch_store_activities_for_manager",
    {
      p_limit: limit ? limit + 1 : 20,
      p_offset: offset ?? 0,
      p_sort_column: "sa_id",
      p_sort_direction: "desc",
      p_filters: rpcFilters as Json,
    },
  );

  if (error) throw error;

  const items = (data ?? []) as StoreActivityForManager[];
  const totalCount = items.length > 0 ? items[0].total_count : 0;
  const hasMore = limit ? items.length > limit : false;
  const limitedData = limit ? items.slice(0, limit) : items;

  return { data: limitedData, hasMore, totalCount };
}

/**
 * Fetch distinct filter values for store activities via server-side RPC.
 * One function handles all filter keys (products, suppliers, stores, periods, reps).
 * The aggregation (SELECT DISTINCT) is done entirely in the database, avoiding
 * the Supabase default row-limit truncation that occurred with frontend aggregation.
 */
export async function fetchDistinctValuesForStoreActivities(
  filterKey: string,
  otherFilters?: Record<string, unknown>,
): Promise<{ value: string | number; label: string }[]> {
  const { data, error } = await supabase.rpc(
    "get_distinct_values_for_store_activities",
    {
      p_filter_key: filterKey,
      p_other_filters: (otherFilters ?? {}) as Record<string, unknown> & Json,
    },
  );

  if (error) throw error;

  // The RPC returns { value: string, label: string }.
  // Parse numeric values back to numbers for filter keys that use numeric IDs.
  const numericKeys = new Set([
    "period_ids",
    "product_ids",
    "supplier_ids",
    "store_ids",
    "agency_ids",
  ]);

  return (data ?? []).map((row: { value: string; label: string }) => ({
    value: numericKeys.has(filterKey) ? Number(row.value) : row.value,
    label: row.label,
  }));
}
